import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMaquinas } from '@/hooks/useMaquinas';
import { useFilaProducao, useIniciarProducao, useFinalizarProducao } from '@/hooks/useProducao';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { ArrowLeft, Play, Square, Pause } from 'lucide-react';
import type { Maquina, StatusMaquina } from '@/types';

const statusConfig: Record<StatusMaquina, { label: string; color: string }> = {
  DISPONIVEL: { label: 'Disponível', color: 'bg-fort3d-verde' },
  IMPRIMINDO: { label: 'Imprimindo', color: 'bg-fort3d-laranja' },
  MANUTENCAO: { label: 'Manutenção', color: 'bg-fort3d-amarelo' },
  INATIVA: { label: 'Inativa', color: 'bg-fort3d-cinza-claro' },
};

export function Impressao() {
  const navigate = useNavigate();
  const { data: maquinas } = useMaquinas();
  const [maquinaSelecionada, setMaquinaSelecionada] = useState<string>('');
  const { data: fila } = useFilaProducao(maquinaSelecionada);
  const iniciarProducao = useIniciarProducao();
  const finalizarProducao = useFinalizarProducao();

  return (
    <div className="min-h-screen bg-fort3d-preto p-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6 pt-4">
          <button onClick={() => navigate('/modo-fabrica')} className="text-white">
            <ArrowLeft className="h-6 w-6" />
          </button>
          <h1 className="text-2xl font-bold text-white">Impressão</h1>
        </div>

        {/* Seleção de Máquina */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Selecione a Máquina</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {maquinas?.map((maquina: Maquina) => (
                <button
                  key={maquina.id}
                  onClick={() => setMaquinaSelecionada(maquina.id)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    maquinaSelecionada === maquina.id
                      ? 'border-fort3d-laranja bg-fort3d-laranja/10'
                      : 'border-fort3d-cinza-medio bg-fort3d-cinza-escuro'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-3 h-3 rounded-full ${statusConfig[maquina.status].color}`} />
                    <span className="text-white font-medium">{maquina.nome}</span>
                  </div>
                  <span className="text-xs text-fort3d-cinza-claro">
                    {statusConfig[maquina.status].label}
                  </span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Fila de Produção */}
        {maquinaSelecionada && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Fila de Produção</CardTitle>
            </CardHeader>
            <CardContent>
              {fila?.length === 0 ? (
                <div className="text-center py-8 text-fort3d-cinza-claro">
                  Nenhuma ordem na fila
                </div>
              ) : (
                <div className="space-y-4">
                  {fila?.map((ordem: any) => (
                    <div
                      key={ordem.id}
                      className="p-4 rounded-xl border border-fort3d-cinza-medio bg-fort3d-cinza-escuro"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-mono text-fort3d-ciano text-sm">{ordem.numeroOP}</p>
                          <p className="text-white font-medium">{ordem.produto?.nome}</p>
                          <p className="text-fort3d-cinza-claro text-sm">
                            Qtd: {ordem.quantidadeSolicitada}
                          </p>
                        </div>
                        <Badge
                          variant={ordem.status === 'PRODUCAO' ? 'warning' : 'secondary'}
                        >
                          {ordem.status === 'PRODUCAO' ? 'Em Andamento' : 'Aguardando'}
                        </Badge>
                      </div>

                      {/* Botões de Ação */}
                      <div className="flex gap-2">
                        {ordem.status === 'NOVO' && (
                          <Button
                            variant="nuvem"
                            size="sm"
                            className="flex-1"
                            leftIcon={<Play className="h-4 w-4" />}
                            onClick={() => iniciarProducao.mutate({ id: ordem.id })}
                            isLoading={iniciarProducao.isPending}
                          >
                            INICIAR
                          </Button>
                        )}
                        {ordem.status === 'PRODUCAO' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              leftIcon={<Pause className="h-4 w-4" />}
                            >
                              PAUSAR
                            </Button>
                            <Button
                              variant="nuvem"
                              size="sm"
                              className="flex-1"
                              leftIcon={<Square className="h-4 w-4" />}
                              onClick={() =>
                                finalizarProducao.mutate({
                                  id: ordem.id,
                                  data: {
                                    quantidadeBoa: ordem.quantidadeSolicitada,
                                    quantidadeRefugada: 0,
                                    progressoPercentual: 100,
                                    tempoRealMinutos: 120,
                                  },
                                })
                              }
                              isLoading={finalizarProducao.isPending}
                            >
                              FINALIZAR
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
